// File: src/components/SearchBar.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCity } from '../context/CityContext';
import { fetchSearchSuggestions } from '../services/car';
import '../styles/SearchBar.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const FUEL_TYPES         = ['petrol', 'diesel', 'cng', 'electric', 'hybrid'];
const TRANSMISSION_TYPES = ['automatic', 'manual', 'amt', 'cvt'];
const BODY_TYPES         = ['suv', 'sedan', 'hatchback', 'muv', 'coupe', 'convertible', 'minivan'];

// Budget keywords → exact backend budget value
const BUDGET_KEYWORD_MAP = [
  { keywords: ['under 3', 'under 3 lakh', 'below 3', 'upto 3', '3 lakh', '3lakh'], value: 'Under ₹3 Lakh' },
  { keywords: ['3 to 5', '3-5', '3 5 lakh', '3 lakh to 5', '4 lakh'],              value: '₹3-5 Lakh'     },
  { keywords: ['5 to 8', '5-8', '5 lakh to 8', '6 lakh', '7 lakh'],                value: '₹5-8 Lakh'     },
  { keywords: ['8 to 12', '8-12', '8 lakh to 12', '10 lakh', '9 lakh'],             value: '₹8-12 Lakh'    },
  { keywords: ['12 to 15', '12-15', '12 lakh to 15', '13 lakh', '14 lakh'],         value: '₹12-15 Lakh'   },
  { keywords: ['above 15', 'above 15 lakh', 'more than 15', 'over 15', '15 lakh+'], value: 'Above ₹15 Lakh'},
];

const BUDGET_PRESETS = [
  { label: 'Under ₹3 Lakh',  value: 'Under ₹3 Lakh',  example: 'e.g. "under 3 lakh"'  },
  { label: '₹3–5 Lakh',      value: '₹3-5 Lakh',      example: 'e.g. "3 to 5 lakh"'   },
  { label: '₹5–8 Lakh',      value: '₹5-8 Lakh',      example: 'e.g. "5 to 8 lakh"'   },
  { label: '₹8–12 Lakh',     value: '₹8-12 Lakh',     example: 'e.g. "8 to 12 lakh"'  },
  { label: '₹12–15 Lakh',    value: '₹12-15 Lakh',    example: 'e.g. "12 to 15 lakh"' },
  { label: 'Above ₹15 Lakh', value: 'Above ₹15 Lakh', example: 'e.g. "above 15 lakh"' },
];

// Popular: use model= for exact model matches, brand= for brand, q= for general
const POPULAR_SEARCHES = [
  { label: 'Swift',         icon: '🚗', param: 'model', value: 'Swift',         hint: 'Model'        },
  { label: 'Hyundai Creta', icon: '🚗', param: 'model', value: 'Creta',         hint: 'Brand+Model'  },
  { label: 'Tata Nexon',    icon: '🚗', param: 'model', value: 'Nexon',         hint: 'Model'        },
  { label: 'Honda City',    icon: '🚗', param: 'model', value: 'City',          hint: 'Model'        },
  { label: 'Hyundai i20',   icon: '🚗', param: 'model', value: 'i20',           hint: 'Model'        },
  { label: 'Maruti Baleno', icon: '🚗', param: 'model', value: 'Baleno',        hint: 'Model'        },
];

// Fuel examples shown in idle dropdown
const FUEL_EXAMPLES = [
  { label: 'Petrol',   value: 'petrol',   icon: '⛽', hint: 'e.g. type "petrol"'   },
  { label: 'Diesel',   value: 'diesel',   icon: '⛽', hint: 'e.g. type "diesel"'   },
  { label: 'CNG',      value: 'cng',      icon: '⛽', hint: 'e.g. type "cng"'      },
  { label: 'Electric', value: 'electric', icon: '⚡', hint: 'e.g. type "electric"' },
  { label: 'Hybrid',   value: 'hybrid',   icon: '🔋', hint: 'e.g. type "hybrid"'   },
];

// Body type examples
const BODY_EXAMPLES = [
  { label: 'SUV',       value: 'suv',       icon: '🚙', hint: 'e.g. type "suv"'       },
  { label: 'Sedan',     value: 'sedan',     icon: '🚘', hint: 'e.g. type "sedan"'     },
  { label: 'Hatchback', value: 'hatchback', icon: '🚗', hint: 'e.g. type "hatchback"' },
  { label: 'MUV',       value: 'muv',       icon: '🚐', hint: 'e.g. type "muv"'       },
];

const PLACEHOLDERS = [
  'Search by brand, model',
  'Search by Petrol SUV',
  'Search by Under 5 Lakh',
  'Search by Automatic 2020',
  'Search by fuel type',
  'Search by transmission',
];

const TYPE_LABELS = {
  brand: 'Brand', model: 'Model', fuelType: 'Fuel Type',
  transmission: 'Transmission', bodyType: 'Body Type', color: 'Color',
};

const MAX_RECENT = 5;
const RECENT_KEY = 'aucwini_recent_searches';

// ─── Highlight matching text ──────────────────────────────────────────────────
function highlightText(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="sb-highlight">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ─── Smart filter detection from free text ────────────────────────────────────
// Returns { paramKey, paramValue } or null
function detectFilter(raw) {
  const text  = raw.trim();
  const lower = text.toLowerCase();

  // ── Year (4-digit) ──
  if (/^\d{4}$/.test(text)) {
    const yr = parseInt(text);
    if (yr >= 1990 && yr <= new Date().getFullYear() + 1)
      return { paramKey: 'year', paramValue: text };
  }

  // ── Budget keywords ──
  for (const preset of BUDGET_KEYWORD_MAP) {
    if (preset.keywords.some(kw => lower.includes(kw)))
      return { paramKey: 'budget', paramValue: preset.value };
  }

  // ── Exact fuel type ──
  const fuel = FUEL_TYPES.find(f =>
    lower === f || lower === `${f} car` || lower === `${f} cars` ||
    lower === `${f} only` || lower.startsWith(`${f} `)
  );
  if (fuel) return { paramKey: 'fuelType', paramValue: fuel };

  // ── Exact transmission ──
  const trans = TRANSMISSION_TYPES.find(t => lower === t || lower === `${t} car`);
  if (trans) return { paramKey: 'transmission', paramValue: trans };

  // ── Exact body type ──
  const body = BODY_TYPES.find(b => lower === b || lower === `${b} car` || lower === `${b} cars`);
  if (body) return { paramKey: 'bodyType', paramValue: body };

  return null;
}

// ─── SearchBar Component ──────────────────────────────────────────────────────
const SearchBar = () => {
  const [inputValue, setInputValue]     = useState('');
  const [isOpen, setIsOpen]             = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [suggestions, setSuggestions]   = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [activePlaceholder, setActivePlaceholder] = useState(0);
  const [recentSearches, setRecentSearches]       = useState([]);

  const { selectedCity } = useCity();
  const navigate         = useNavigate();
  const wrapperRef       = useRef(null);
  const inputRef         = useRef(null);
  const debounceRef      = useRef(null);

  // ── Load recents ──
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecentSearches(Array.isArray(stored) ? stored.slice(0, MAX_RECENT) : []);
    } catch { setRecentSearches([]); }
  }, []);

  const saveToRecent = (label) => {
    if (!label?.trim()) return;
    setRecentSearches(prev => {
      const filtered = prev.filter(r => r.toLowerCase() !== label.toLowerCase());
      const updated  = [label, ...filtered].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  const removeRecent = (e, term) => {
    e.stopPropagation();
    setRecentSearches(prev => {
      const updated = prev.filter(r => r !== term);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(updated)); } catch {}
      return updated;
    });
  };

  // ── Animated placeholder ──
  useEffect(() => {
    const timer = setInterval(() => {
      setActivePlaceholder(p => (p + 1) % PLACEHOLDERS.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // ── Close on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Fetch backend suggestions ──
  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim()) { setSuggestions([]); return; }
    setIsLoading(true);
    try {
      const results = await fetchSearchSuggestions(query);
      setSuggestions(results || []);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setHighlightIdx(-1);
    setIsOpen(true);
    clearTimeout(debounceRef.current);
    if (val.trim().length > 0) {
      debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
    } else {
      setSuggestions([]);
    }
  };

  // ── CORE: Fresh navigation — one filter + city, nothing else ──
  const navigateFresh = (paramKey, paramValue, recentLabel) => {
    const params = new URLSearchParams();
    params.set('location', selectedCity);
    if (paramKey && paramValue) params.set(paramKey, String(paramValue));
    if (recentLabel) saveToRecent(recentLabel);
    navigate(`/buy-cars?${params.toString()}`);
    setInputValue('');
    setIsOpen(false);
    setSuggestions([]);
    setHighlightIdx(-1);
  };

  // ── Free text search — smart detection first ──
  const handleFreeTextSearch = (text = inputValue) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    // Try smart detection
    const detected = detectFilter(trimmed);
    if (detected) {
      navigateFresh(detected.paramKey, detected.paramValue, trimmed);
      return;
    }

    // Default: free text q= (backend handles brand/model fuzzy)
    navigateFresh('q', trimmed, trimmed);
  };

  // ── Suggestion click — use correct param from backend type ──
  const handleSuggestionClick = (suggestion) => {
    const paramMap = {
      brand:        'brand',
      model:        'model',
      fuelType:     'fuelType',
      transmission: 'transmission',
      bodyType:     'bodyType',
      color:        'color',
    };
    const paramKey = paramMap[suggestion.type] || 'q';
    // Use exact name from suggestion for precise matching
    navigateFresh(paramKey, suggestion.name, suggestion.name);
  };

  // ── Keyboard nav ──
  const handleKeyDown = (e) => {
    const allItems = getKeyboardItems();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && allItems[highlightIdx]) {
        allItems[highlightIdx].onSelect();
      } else {
        handleFreeTextSearch(inputValue);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const getKeyboardItems = () => {
    if (inputValue.trim()) {
      const items = [{ onSelect: () => handleFreeTextSearch(inputValue) }];
      suggestions.forEach(s => items.push({ onSelect: () => handleSuggestionClick(s) }));
      return items;
    }
    return [
      ...recentSearches.map(r => ({ onSelect: () => navigateFresh('q', r, r) })),
      ...POPULAR_SEARCHES.map(p => ({ onSelect: () => navigateFresh(p.param, p.value, p.label) })),
    ];
  };

  return (
    <div className="sb-wrapper" ref={wrapperRef}>

      {/* ── Search Box ──────────────────────────────────────────── */}
      <div
        className={`sb-box ${isOpen ? 'sb-box--open' : ''}`}
        onClick={() => { setIsOpen(true); inputRef.current?.focus(); }}
      >
        <span className="sb-left-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </span>

        <input
          ref={inputRef}
          type="text"
          className="sb-input"
          placeholder={PLACEHOLDERS[activePlaceholder]}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />

        {inputValue.length > 0 && (
          <button
            className="sb-clear-btn"
            onMouseDown={(e) => {
              e.preventDefault(); e.stopPropagation();
              setInputValue(''); setSuggestions([]); setHighlightIdx(-1);
              inputRef.current?.focus();
            }}
            aria-label="Clear"
          >×</button>
        )}

        <button
          className="sb-search-btn"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleFreeTextSearch(); }}
          aria-label="Search"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sb-btn-icon">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </button>
      </div>

      {/* ── Dropdown ────────────────────────────────────────────── */}
      {isOpen && (
        <div className="sb-dropdown">

          {/* ── TYPING: Spinny-style suggestion list ── */}
          {inputValue.trim() ? (
            <div className="sb-suggestions-list">

              {/* Top: "Search X in City" */}
              <div
                className={`sb-sugg-row sb-sugg-row--query ${highlightIdx === 0 ? 'sb-sugg-row--active' : ''}`}
                onMouseDown={() => handleFreeTextSearch(inputValue)}
              >
                <span className="sb-sugg-left-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                </span>
                <span className="sb-sugg-name">{inputValue}</span>
                <span className="sb-sugg-in-city">in {selectedCity}</span>
                <span className="sb-sugg-arrow-icon">↗</span>
              </div>

              {isLoading && (
                <div className="sb-loading-row">
                  <span className="sb-spinner" />
                  <span>Searching…</span>
                </div>
              )}

              {!isLoading && suggestions.length > 0 && (
                <>
                  <div className="sb-result-count-header">
                    {suggestions.length} Search Result{suggestions.length !== 1 ? 's' : ''}
                  </div>
                  {suggestions.map((s, idx) => (
                    <div
                      key={`${s.type}-${s.name}-${idx}`}
                      className={`sb-sugg-row ${highlightIdx === idx + 1 ? 'sb-sugg-row--active' : ''}`}
                      onMouseDown={() => handleSuggestionClick(s)}
                    >
                      <span className="sb-sugg-left-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                      </span>
                      <span className="sb-sugg-name">{highlightText(s.name, inputValue)}</span>
                      <span className="sb-sugg-type-tag">{TYPE_LABELS[s.type] || s.type}</span>
                      <span className="sb-sugg-arrow-icon">↗</span>
                    </div>
                  ))}
                </>
              )}

              {!isLoading && suggestions.length === 0 && (
                <div className="sb-no-results">
                  No results in {selectedCity} — press Enter to search anyway
                </div>
              )}
            </div>

          ) : (
            /* ── IDLE: Spinny-style list with examples ── */
            <div className="sb-idle-content">

              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div className="sb-idle-section">
                  <div className="sb-idle-section-title">Recent Searches</div>
                  {recentSearches.map((term, i) => (
                    <div
                      key={i}
                      className={`sb-idle-row ${highlightIdx === i ? 'sb-idle-row--active' : ''}`}
                      onMouseDown={() => navigateFresh('q', term, term)}
                    >
                      <span className="sb-idle-icon sb-idle-icon--clock">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </span>
                      <span className="sb-idle-label">{term}</span>
                      <button className="sb-recent-remove" onMouseDown={(e) => removeRecent(e, term)} aria-label="Remove">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Popular Searches */}
              <div className="sb-idle-section">
                <div className="sb-idle-section-title">Popular Searches</div>
                {POPULAR_SEARCHES.map((p, i) => (
                  <div
                    key={i}
                    className={`sb-idle-row ${highlightIdx === recentSearches.length + i ? 'sb-idle-row--active' : ''}`}
                    onMouseDown={() => navigateFresh(p.param, p.value, p.label)}
                  >
                    <span className="sb-idle-icon sb-idle-icon--trend">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                        <polyline points="17 6 23 6 23 12"/>
                      </svg>
                    </span>
                    <span className="sb-idle-label">{p.label}</span>
                    <span className="sb-idle-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>

              {/* Budget — show one example so user knows how to type */}
              <div className="sb-idle-section">
                <div className="sb-idle-section-title">
                  Budget
                  <span className="sb-idle-hint">click or type e.g. "under 3 lakh"</span>
                </div>
                {BUDGET_PRESETS.map((b, i) => (
                  <div
                    key={i}
                    className="sb-idle-row"
                    onMouseDown={() => navigateFresh('budget', b.value, b.label)}
                  >
                    <span className="sb-idle-icon sb-idle-icon--budget">₹</span>
                    <span className="sb-idle-label">{b.label}</span>
                    <span className="sb-idle-example">{b.example}</span>
                    <span className="sb-idle-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>

              {/* Fuel Type — one example per item */}
              <div className="sb-idle-section">
                <div className="sb-idle-section-title">
                  Fuel Type
                  <span className="sb-idle-hint">click or type e.g. "petrol"</span>
                </div>
                {FUEL_EXAMPLES.map((f, i) => (
                  <div
                    key={i}
                    className="sb-idle-row"
                    onMouseDown={() => navigateFresh('fuelType', f.value, f.label)}
                  >
                    <span className="sb-idle-icon">{f.icon}</span>
                    <span className="sb-idle-label">{f.label}</span>
                    <span className="sb-idle-example">{f.hint}</span>
                    <span className="sb-idle-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>

              {/* Body Type */}
              <div className="sb-idle-section">
                <div className="sb-idle-section-title">
                  Body Type
                  <span className="sb-idle-hint">click or type e.g. "suv"</span>
                </div>
                {BODY_EXAMPLES.map((b, i) => (
                  <div
                    key={i}
                    className="sb-idle-row"
                    onMouseDown={() => navigateFresh('bodyType', b.value, b.label)}
                  >
                    <span className="sb-idle-icon">{b.icon}</span>
                    <span className="sb-idle-label">{b.label}</span>
                    <span className="sb-idle-example">{b.hint}</span>
                    <span className="sb-idle-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>

              {/* Transmission */}
              <div className="sb-idle-section">
                <div className="sb-idle-section-title">
                  Transmission
                  <span className="sb-idle-hint">click or type e.g. "automatic"</span>
                </div>
                {[
                  { label: 'Automatic', value: 'automatic', icon: '⚙️', hint: 'e.g. type "automatic"' },
                  { label: 'Manual',    value: 'manual',    icon: '🔧', hint: 'e.g. type "manual"'    },
                ].map((t, i) => (
                  <div
                    key={i}
                    className="sb-idle-row"
                    onMouseDown={() => navigateFresh('transmission', t.value, t.label)}
                  >
                    <span className="sb-idle-icon">{t.icon}</span>
                    <span className="sb-idle-label">{t.label}</span>
                    <span className="sb-idle-example">{t.hint}</span>
                    <span className="sb-idle-arrow">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </span>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;